import { formatDateTimeUrlDate, urlDateFormat } from 'common/UrlDate';
import { DateTime } from 'luxon';
import { useSelector } from 'src/store';
import {
  selectCart,
  selectShefAvailabilityWithCapacityForLineItems,
  selectShefDeliveryDateCapacityInfo,
} from 'src/store/cart/selectors';
import { DEFAULT_CAPACITY_INFO } from 'src/store/cart/utils/capacity';
import { IAvailabilityInfo } from '../components/types';

interface IUseIsSoldOutArgs {
  shefId: string;
  availabilityInfo: IAvailabilityInfo[];
  deliveryDate?: DateTime | null;
  isEditingOrder: boolean;
}
export const useIsSoldOut = ({ availabilityInfo, deliveryDate, shefId, isEditingOrder }: IUseIsSoldOutArgs) => {
  const hasNoAvailability = !availabilityInfo.length;
  const notSoldOutDays = availabilityInfo.filter((day) => !day.isSoldOut);
  const allSoldOut = notSoldOutDays.length === 0;
  const selectedDate = availabilityInfo.find((day) => deliveryDate && day.dateTime.equals(deliveryDate));
  const selectedDaySoldOut = Boolean(selectedDate?.isSoldOut);
  const selectedDateUnavailable = !selectedDate;
  const modalVisible = hasNoAvailability || allSoldOut || selectedDaySoldOut;

  const { capacityInfo, datesWithCartCapacity } = useSelector((state) => {
    const cart = selectCart(state, isEditingOrder);
    const datesWithCartCapacity = selectShefAvailabilityWithCapacityForLineItems(cart, shefId);
    if (!deliveryDate) {
      return {
        capacityInfo: DEFAULT_CAPACITY_INFO,
        datesWithCartCapacity,
      };
    }
    const deliveryUrlDate = deliveryDate.toFormat(urlDateFormat);
    return {
      capacityInfo: selectShefDeliveryDateCapacityInfo(cart, shefId, isEditingOrder, deliveryUrlDate),
      datesWithCartCapacity,
    };
  });
  const { numDishesAvailable, numDishesRemaining } = capacityInfo;
  const isOrderingAllRemainingItems = numDishesAvailable > 0 && numDishesRemaining <= 0;
  const isOrderingMoreThanDeliveryDateCapacity = numDishesRemaining < 0;
  const cartValidDays = availabilityInfo.filter(({ dateTime }) => {
    const urlDate = formatDateTimeUrlDate(dateTime);
    return !!datesWithCartCapacity?.has(urlDate);
  });

  return {
    hasNoAvailability,
    allSoldOut,
    isOrderingAllRemainingItems,
    isOrderingMoreThanDeliveryDateCapacity,
    selectedDaySoldOut,
    selectedDateUnavailable,
    notSoldOutDays,
    cartValidDays,
    modalVisible,
    numDishesRemaining,
  };
};
